import { Drawer as AntDrawer, DrawerProps as AntDrawerProps, theme } from 'antd';
import styled from 'styled-components';
import { ReactNode } from 'react';
import type { GlobalToken } from 'antd';

export type DrawerPlacement = 'top' | 'right' | 'bottom' | 'left';
export type DrawerSize = 'small' | 'medium' | 'large';

const sizeMap: Record<DrawerSize, number> = {
  small: 320,
  medium: 480,
  large: 720,
};

export interface DrawerProps
  extends Omit<AntDrawerProps, 'size' | 'width' | 'height'> {
  /** Whether the drawer is open */
  open: boolean;
  /** Called when drawer should close */
  onClose: () => void;
  /** Title of the drawer */
  title?: ReactNode;
  /** Drawer placement */
  placement?: DrawerPlacement;
  /** Drawer size (presets) */
  size?: DrawerSize;
  /** Custom width (overrides size for left/right placement) */
  width?: number | string;
  /** Custom height (overrides size for top/bottom placement) */
  height?: number | string;
  /** Footer content */
  footer?: ReactNode;
  /** Whether to show close icon */
  closable?: boolean;
  /** Whether clicking mask closes drawer */
  maskClosable?: boolean;
  /** Whether to destroy drawer content on close */
  destroyOnClose?: boolean;
  /** Drawer content */
  children: ReactNode;
}

interface StyledDrawerProps {
  $token: GlobalToken;
}

const StyledDrawer = styled(AntDrawer)<StyledDrawerProps>`
  .ant-drawer-header {
    border-bottom: 1px solid ${({ $token }) => $token.colorSplit};
  }

  .ant-drawer-close {
    transition: color ${({ $token }) => $token.motionDurationFast};

    &:hover {
      color: ${({ $token }) => $token.colorText};
    }
  }

  .ant-drawer-footer {
    border-top: 1px solid ${({ $token }) => $token.colorSplit};
  }
`;

/**
 * Drawer component based on Libertify Design System
 *
 * A panel that slides in from the edge of the screen.
 * Styled via antdTheme configuration for consistent theming.
 *
 * @example
 * ```tsx
 * import { Drawer, Button } from '@libertify/ui';
 * import { useState } from 'react';
 *
 * const MyComponent = () => {
 *   const [open, setOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setOpen(true)}>Open Drawer</Button>
 *       <Drawer
 *         open={open}
 *         onClose={() => setOpen(false)}
 *         title="Drawer Title"
 *         footer={<Button onClick={() => setOpen(false)}>Close</Button>}
 *       >
 *         <p>Drawer content goes here</p>
 *       </Drawer>
 *     </>
 *   );
 * };
 * ```
 */
export const Drawer = ({
  open,
  onClose,
  title,
  placement = 'right',
  size = 'medium',
  width,
  height,
  footer,
  closable = true,
  maskClosable = true,
  destroyOnClose = false,
  children,
  ...props
}: DrawerProps) => {
  const { token } = theme.useToken();

  // Calculate dimensions based on placement
  const isHorizontal = placement === 'left' || placement === 'right';
  const dimension = isHorizontal
    ? (width ?? sizeMap[size])
    : (height ?? sizeMap[size]);

  return (
    <StyledDrawer
      $token={token}
      open={open}
      onClose={onClose}
      title={title}
      placement={placement}
      width={isHorizontal ? dimension : undefined}
      height={isHorizontal ? undefined : dimension}
      footer={footer}
      closable={closable}
      maskClosable={maskClosable}
      destroyOnClose={destroyOnClose}
      {...props}
    >
      {children}
    </StyledDrawer>
  );
};

export default Drawer;
